import './styles/index.scss';
import 'rsuite/dist/rsuite.css';
import '@fontsource/lato';

import { useEffect, useMemo, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { CookiesProvider } from 'react-cookie';
import { CustomProvider } from 'rsuite';
import * as locales from 'rsuite/locales';

import Routing from './routing';

import { useTranslation } from 'react-i18next';
import '@monorepo/shared/i18n/config';

import { getNavigatorLanguage, useAppSelector } from '@monorepo/shared';
import { selectCurrentLocale } from '@monorepo/shared/store/authSlice';

import { jaJP } from '@monorepo/shared/i18n/jaJP/rsLocale';

// CSP nonce from meta tag — set by nginx at runtime (replaces d35a621f293996fd33bbf4f11cbc2930af9962431dfac02999e5987e37d0832b)
const cspNonce =
  typeof document !== 'undefined'
    ? (
        document.querySelector(
          'meta[property="csp-nonce"]'
        ) as HTMLElement | null
      )?.nonce
    : undefined;

function App() {
  const isDarkTheme = false;
  const [darkMode, setDarkMode] = useState(isDarkTheme);

  const { i18n } = useTranslation();

  const currentLocale = useAppSelector(selectCurrentLocale);

  // merge locales once (avoid re-creating every render)
  const languages: Record<string, any> = useMemo(() => {
    return {
      ...locales,
      jaJP
    };
  }, []);

  // normalize locale
  const resolvedLocaleKey = useMemo(() => {
    const locale =
      currentLocale === 'auto' ? getNavigatorLanguage() : currentLocale;

    return languages[locale] ? locale : 'enUS';
  }, [currentLocale, languages]);

  const localeVal = useMemo(() => {
    return languages[resolvedLocaleKey];
  }, [languages, resolvedLocaleKey]);

  // sync i18n safely
  useEffect(() => {
    i18n.changeLanguage(resolvedLocaleKey);
  }, [resolvedLocaleKey, i18n]);

  return (
    <CookiesProvider>
      <HelmetProvider>
        <CustomProvider locale={localeVal} csp={{ nonce: cspNonce }}>
          <Routing darkMode={darkMode} setDarkMode={setDarkMode} />
        </CustomProvider>
      </HelmetProvider>
    </CookiesProvider>
  );
}

export default App;
